import type { ColDef } from "~/components/grid/AgGrid";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === "";

const toText = (value: unknown): string => {
	if (Array.isArray(value)) return value.map(toText).join(", ");
	if (typeof value === "string") return value;
	return String(value);
};

const compareNonBlank = (a: unknown, b: unknown): number => {
	if (typeof a === "number" && typeof b === "number") {
		if (Number.isNaN(a) || Number.isNaN(b)) return Number.isNaN(a) === Number.isNaN(b) ? 0 : Number.isNaN(a) ? 1 : -1;
		return a - b;
	}
	if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
	if (typeof a === "bigint" && typeof b === "bigint") return a < b ? -1 : a > b ? 1 : 0;
	if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
	return collator.compare(toText(a), toText(b));
};

// AG Grid negates the result when descending; the set filter calls this as (a, b), so it must not read nodes.
const defaultGridComparator = (
	a: unknown,
	b: unknown,
	_nodeA?: unknown,
	_nodeB?: unknown,
	isDescending?: boolean
): number => {
	const aBlank = isBlank(a);
	const bBlank = isBlank(b);
	if (aBlank || bBlank) {
		if (aBlank && bBlank) return 0;
		const blankAfter = isDescending ? -1 : 1;
		return aBlank ? blankAfter : -blankAfter;
	}
	return compareNonBlank(a, b);
};

export const compareGridLabels = (
	a: null | string | undefined,
	b: null | string | undefined,
	isDescending?: boolean
): number => defaultGridComparator(a, b, undefined, undefined, isDescending);

export const DEFAULT_GRID_COMPARATOR = defaultGridComparator as NonNullable<ColDef["comparator"]>;

export default defaultGridComparator;
